"use client";

import { useEffect, useState, lazy, Suspense, startTransition, useRef, useCallback } from "react";
import { registerActiveRecordingCheck } from "@/lib/navigation/backButtonWarning";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { PostPlayer, type PostPlayerRef } from "@/components/audio/PostPlayer";
import { ReplyCard } from "@/components/post/ReplyCard";
import { PostCardActions } from "@/components/post/PostCardActions";
import { DeleteConfirmModal } from "@/components/post/DeleteConfirmModal";
import { RecordingLossConfirmModal } from "@/components/recording/RecordingLossConfirmModal";
import { setNavigationConfirmed } from "@/components/recording/RecorderPanel";
import { type EncodedAudio } from "@/lib/audio/encoding";
import { getLocalUserId } from "@/lib/identity/client";
import { resolveIdentityOnce, useIdentity } from "@/lib/identity/useIdentity";
import { filterDeletedLeafReplies, type DeletedByType } from "@/lib/tombstone";
import { useAdminStatus } from "@/lib/admin/useAdminStatus";
import { AudioPlayerProvider } from "@/lib/audio/AudioPlayerContext";
import { postCardStyles } from "@/lib/styles/post-card-styles";
import { Flag, ExternalLink, X } from "lucide-react";

// Lazy load heavy components that are only shown conditionally
// Using startTransition to mark lazy loads as non-urgent, reducing TBT impact
const RecorderPanel = lazy(() => 
  import("@/components/recording/RecorderPanel").then(mod => ({ default: mod.RecorderPanel }))
);
const ReportPostModal = lazy(() => 
  import("@/components/report/ReportPostModal").then(mod => ({ default: mod.ReportPostModal }))
);

interface Post {
  id: string;
  user_id: string;
  pseudonym: string;
  audio_url: string | null;
  duration_ms: number;
  waveform_url: string | null;
  og_image_url: string | null;
  views: number;
  reply_count: number;
  created_at: string;
  slug: string;
  deleted?: boolean;
  deleted_by_type?: DeletedByType; // Optional, defaults to 'user' if not provided
  deleted_at?: string;
}

interface Reply {
  id: string;
  user_id: string;
  pseudonym: string;
  audio_url: string | null;
  duration_ms: number;
  created_at: string;
  parent_id: string;
  slug: string;
  deleted_by_type?: DeletedByType; // Optional, defaults to 'user' if not provided
  replies?: Reply[]; // Nested replies
}

export default function PostPage() {
  // Extract id immediately to avoid React DevTools serialization issues
  const id = (useParams().id as string) || '';
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    identity,
    rerollPseudonym,
    refreshIdentity,
    loading: identityLoading,
    error: identityError,
  } = useIdentity();
  // Check admin status - user actions are disabled when admin is logged in
  const { isAdmin } = useAdminStatus();
  const [post, setPost] = useState<Post | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track which recorder is open: null, "main" for main post, or reply ID
  const [openRecorderId, setOpenRecorderId] = useState<string | null>(null);
  // Track recorder state per recorder ID to prevent opening new recorder when there's unsaved content
  const [recorderStates, setRecorderStates] = useState<Record<string, "idle" | "recording" | "processing" | "preview" | "uploading">>({});
  const [copied, setCopied] = useState(false);
  const [collapseAllTrigger, setCollapseAllTrigger] = useState(0);
  const [reportTarget, setReportTarget] = useState<{ postId: string; pseudonym: string } | null>(null);
  const [highlightedReplyId, setHighlightedReplyId] = useState<string | null>(null);
  const [ellipsisMenuOpen, setEllipsisMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null); // For main reply button
  const [replyRateLimitErrors, setReplyRateLimitErrors] = useState<Record<string, string>>({}); // For individual reply buttons
  const [recordingLossModalOpen, setRecordingLossModalOpen] = useState(false);
  const [pendingRecorderAction, setPendingRecorderAction] = useState<{ action: () => void; id: string; state?: "recording" | "preview"; isClosing?: boolean } | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const postPlayerRef = useRef<PostPlayerRef>(null);
  // Ref to track recorder states synchronously (for beforeunload prevention)
  const recorderStatesRef = useRef<Record<string, "idle" | "recording" | "processing" | "preview" | "uploading">>({});
  // Ref to store continueOpenRecorder function so it's always accessible in closures
  const continueOpenRecorderRef = useRef<((id: string | "main") => Promise<void>) | null>(null);
  
  // Refs to track current state for polling (avoid dependency issues)
  const postRef = useRef<Post | null>(null);
  const repliesRef = useRef<Reply[]>([]);
  const pollTriggerRef = useRef<(() => void) | null>(null);
  // Ref to track if we should auto-scroll (only for URL-based highlights, not hover)
  const shouldAutoScrollRef = useRef(false);
  // Refs for main recorder auto-scroll (FR-6.8, FR-6.9)
  const mainRecorderAutoScrollFiredRef = useRef(false);
  const mainRecorderElementRef = useRef<HTMLDivElement | null>(null);
  const mainRecorderState = recorderStates["main"] || "idle";

  // Helper to check if post has been viewed this session
  const hasViewedPost = (postId: string): boolean => {
    try {
      const viewed = sessionStorage.getItem('vocly_viewed_posts');
      if (!viewed) return false;
      const viewedIds: string[] = JSON.parse(viewed);
      return viewedIds.includes(postId);
    } catch {
      return false;
    }
  };

  // Helper to mark post as viewed in session
  const markPostAsViewed = (postId: string): void => {
    try {
      const viewed = sessionStorage.getItem('vocly_viewed_posts');
      const viewedIds: string[] = viewed ? JSON.parse(viewed) : [];
      if (!viewedIds.includes(postId)) {
        viewedIds.push(postId);
        sessionStorage.setItem('vocly_viewed_posts', JSON.stringify(viewedIds));
      }
    } catch {
      // sessionStorage not available, ignore
    }
  };

  // Mark notification as read when audio is played on thread page
  // Only marks the current user's notification for the specific reply
  const markNotificationAsReadForReply = async (replyId: string): Promise<void> => {
    const localUserId = getLocalUserId();
    if (!localUserId) {
      // Silently fail - user might not be logged in or have notifications
      return;
    }

    try {
      const response = await fetch('/api/notifications/batch-read', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          local_user_id: localUserId,
          reply_ids: [replyId],
        }),
      });

      if (!response.ok) {
        // Silently fail - notification might not exist for this user/reply
        // Polling will sync the correct state later
        return;
      }
      // Success - polling will update badge count automatically
    } catch (error) {
      // Silently fail - network errors will be handled by polling
      console.error('Error marking notification as read for reply:', error);
    }
  };


  // Fetch post and replies
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // Only record view if this is the first visit this session
        const isFirstView = !hasViewedPost(id);
        // Check for reply query parameter (for highlighting from notifications)
        const replySlug = searchParams.get('reply');
        
        // Build URL with query parameters
        const urlParams = new URLSearchParams();
        if (isFirstView) {
          urlParams.set('record_view', 'true');
        }
        if (replySlug) {
          urlParams.set('reply', replySlug);
        }
        const queryString = urlParams.toString();
        const url = queryString 
          ? `/api/post/${id}?${queryString}`
          : `/api/post/${id}`;

        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            setError("Post not found");
            document.title = "Post Not Found - Vocly";
          } else {
            setError("Failed to load post");
            document.title = "Error - Vocly";
          }
          return;
        }

        const data = await response.json();
        setPost(data.post);
        setReplies(data.replies || []);
        
        // Update refs to keep them in sync with state
        postRef.current = data.post;
        repliesRef.current = data.replies || [];
        
        // If viewing a reply slug, highlight the reply
        if (data.highlighted_reply_id) {
          setHighlightedReplyId(data.highlighted_reply_id);
          shouldAutoScrollRef.current = true; // Only auto-scroll for URL-based highlights
        }
        
        // Mark as viewed after successful fetch
        markPostAsViewed(id);
        
        // Update page title with pseudonym
        document.title = `${data.post.pseudonym} on Vocly`;
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Failed to load post");
        document.title = "Error - Vocly";
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      void fetchPost();
    }
  }, [id, searchParams]);

  // Scroll to highlighted reply when viewing a reply slug (only for URL-based highlights, not hover)
  useEffect(() => {
    if (!highlightedReplyId || loading || !replies.length) return;
    
    // Only auto-scroll if this is from initial URL load, not from hover
    if (!shouldAutoScrollRef.current) return;
    
    // Reset the flag after scrolling
    shouldAutoScrollRef.current = false;
    
    // Wait for DOM to update, then scroll to the highlighted reply
    // Use multiple attempts with increasing delays to handle nested reply rendering
    const scrollToHighlighted = (attempt = 0) => {
      const replyElement = document.getElementById(`reply-${highlightedReplyId}`);
      if (replyElement) {
        // Small delay to ensure highlighting styles are applied
        setTimeout(() => {
          replyElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else if (attempt < 5) {
        // Retry if element not found yet (nested replies might still be rendering)
        setTimeout(() => scrollToHighlighted(attempt + 1), 200);
      } else {
        console.warn(`Could not find reply element with id: reply-${highlightedReplyId}`);
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      scrollToHighlighted();
    });
  }, [highlightedReplyId, loading, replies.length]);

  // Clear highlight when user clicks anywhere on the page (except on "Reply to" links)
  useEffect(() => {
    if (!highlightedReplyId) return;

    const handleDocumentClick = (e: MouseEvent) => {
      // Don't clear if clicking on a "Reply to" link (let that handle its own highlight)
      const target = e.target as HTMLElement;
      if (target.closest('span[class*="cursor-pointer"]') && target.closest('span[class*="hover:text-[#ff3ea5]"]')) {
        return; // Let the "Reply to" link handle highlighting
      }
      
      // Clear highlight on any other click
      setHighlightedReplyId(null);
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [highlightedReplyId]);

  // Intercept Next.js Link navigation (for header links) to show custom modal
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      // Find the closest anchor or Link component first
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      
      if (!link) {
        return;
      }

      const href = link.getAttribute('href');
      if (!href) {
        return;
      }

      // CRITICAL: Check for active recording using ref (synchronous) before any other checks
      // Flag should already be set proactively by useEffect, but ensure it's set here too
      const hasActiveRecording = Object.values(recorderStatesRef.current).some(
        state => state === "recording" || state === "preview" || state === "uploading"
      );

      if (!hasActiveRecording) {
        return; // No active recording - allow navigation (flag already cleared by useEffect)
      }

      // Don't intercept external links, hash links, or same-page links
      const isExternal = link.target === "_blank" || link.hasAttribute("download");
      const isHash = href.startsWith("#");
      const currentPath = window.location.pathname;
      
      if (isExternal || isHash || href === currentPath) {
        return;
      }

      // Check if it's a same-origin navigation
      try {
        const url = new URL(href, window.location.origin);
        const currentUrl = new URL(window.location.href);
        
        // Only intercept if navigating to a different path
        if (url.origin === currentUrl.origin && url.pathname !== currentUrl.pathname) {
          // Flag already set proactively - prevent navigation and show modal
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Show custom modal
          setPendingNavigation(href);
          setRecordingLossModalOpen(true);
        }
        // If same-page navigation, let it happen (flag stays set for next attempt)
      } catch (err) {
        // Invalid URL, allow default behavior
        // Flag management handled by useEffect based on active recordings
      }
    };

    // Use capture phase to intercept before Next.js Link handles it
    // Capture phase runs before target phase, giving us earliest possible interception
    document.addEventListener('click', handleLinkClick, true);
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, []); // Empty deps - we use ref for synchronous access

  // Poll for new replies from other users (only when page is visible)
  useEffect(() => {
    if (!id || !post) return; // Don't poll until post is loaded
    if (loading) return; // Don't poll while initial load is happening

    let pollInterval: NodeJS.Timeout;
    let isPageVisible = !document.hidden;

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Helper to count total replies in tree (recursive)
    const countTotalReplies = (repliesArray: Reply[]): number => {
      return repliesArray.reduce((count, reply) => {
        return count + 1 + (reply.replies ? countTotalReplies(reply.replies) : 0);
      }, 0);
    };

    // Helper to check if root post was deleted
    const isPostDeleted = (currentPost: Post | null, newPost: Post): boolean => {
      if (!currentPost) return false;
      // Post was deleted if audio_url changed from non-null to null
      return currentPost.audio_url !== null && newPost.audio_url === null;
    };

    // Helper to recursively check if any replies were deleted
    const hasDeletedReplies = (currentReplies: Reply[], newReplies: Reply[]): boolean => {
      // Create a map of current replies by ID for quick lookup
      const currentReplyMap = new Map<string, Reply>();
      const buildMap = (replyList: Reply[]) => {
        replyList.forEach(reply => {
          currentReplyMap.set(reply.id, reply);
          if (reply.replies) {
            buildMap(reply.replies);
          }
        });
      };
      buildMap(currentReplies);

      // Check if any existing reply was deleted
      const checkDeleted = (replyList: Reply[]): boolean => {
        for (const newReply of replyList) {
          const currentReply = currentReplyMap.get(newReply.id);
          if (currentReply) {
            // Check if this reply was deleted (audio_url changed from non-null to null)
            if (currentReply.audio_url !== null && newReply.audio_url === null) {
              return true;
            }
            // Check nested replies
            if (newReply.replies && currentReply.replies) {
              if (checkDeleted(newReply.replies)) {
                return true;
              }
            }
          }
        }
        return false;
      };

      return checkDeleted(newReplies);
    };

    const pollForUpdates = async () => {
      // Skip polling if page is hidden
      if (!isPageVisible) return;
      
      try {
        // Fetch with cache-busting to get latest data
        const response = await fetch(`/api/post/${id}?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
          }
        });
        
        if (!response.ok) return;
        
        const data = await response.json();
        
        // Use refs to get current values (avoids stale closure issues)
        const currentPost = postRef.current;
        const currentReplies = repliesRef.current;
        
        // Compare both reply_count and total replies in tree
        // This catches nested replies that don't change root post's reply_count
        const currentTotalReplies = countTotalReplies(currentReplies);
        const newTotalReplies = countTotalReplies(data.replies || []);
        const replyCountChanged = currentPost ? data.post.reply_count !== currentPost.reply_count : true;
        const totalRepliesChanged = newTotalReplies !== currentTotalReplies;
        
        // Check for deletions
        const postDeleted = isPostDeleted(currentPost, data.post);
        const repliesDeleted = hasDeletedReplies(currentReplies, data.replies || []);
        
        // Update if counts changed OR if deletions detected OR if we don't have current state
        if (replyCountChanged || totalRepliesChanged || postDeleted || repliesDeleted || !currentPost) {
          // Preserve scroll position before updating
          const scrollPosition = window.scrollY;
          
          setPost(data.post);
          setReplies(data.replies || []);
          
          // Update refs to keep them in sync
          postRef.current = data.post;
          repliesRef.current = data.replies || [];
          
          // Update page title if pseudonym changed
          if (currentPost && data.post.pseudonym !== currentPost.pseudonym) {
            document.title = `${data.post.pseudonym} on Vocly`;
          }
          
          // Restore scroll position after state update
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPosition);
          });
        }
      } catch (err) {
        // Silently fail - don't disrupt user experience
        console.error('Polling error:', err);
      }
    };

    // Store poll function in ref so it can be called externally
    pollTriggerRef.current = pollForUpdates;

    // Poll every 15 seconds (balance between freshness and performance)
    pollInterval = setInterval(pollForUpdates, 15000);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      pollTriggerRef.current = null;
    };
  }, [id, post, loading]); // Removed 'replies' to prevent polling from restarting

  // Handle reply publish
  const handleReplyPublish = async (audio: EncodedAudio, parentId: string) => {
    try {
      const localUserId = getLocalUserId();
      if (!localUserId) {
        throw new Error("Failed to get user identity");
      }

      // Ensure user identity is resolved before publishing
      // This handles first-time visitors who haven't published before
      const identity = await resolveIdentityOnce();
      if (!identity) {
        // resolveIdentityOnce will throw a network error if offline, so this shouldn't happen
        // but keep as fallback
        throw new Error("Failed to resolve identity. Please refresh and try again.");
      }

      // Convert blob to base64
      const audioBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          const base64String = base64.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audio.mp3Blob);
      });

      // Create reply
      const response = await fetch("/api/post/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          local_user_id: localUserId,
          audio_base64: audioBase64,
          duration_ms: audio.durationMs,
          parent_id: parentId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Failed to publish reply";
        
        // Treat 503 (Service Unavailable) as a network/connectivity issue
        if (response.status === 503) {
          throw new Error("Not able to connect. Please check your internet connection and try again.");
        }
        
        throw new Error(errorMessage);
      }

      const createResult = await response.json();
      const newReplyId = createResult.post_id; // Store the ID of the newly created reply

      // Capture current recorder ID before clearing (for state cleanup)
      const currentRecorderId = openRecorderId;

      // Close the recorder immediately for better UX
      setOpenRecorderId(null);

      // Clean up recorder state to prevent stale navigation warnings
      // The RecorderPanel's onStateChange("idle") effect may not fire if it unmounts first
      if (currentRecorderId) {
        setRecorderStates(prev => {
          const updated = { ...prev };
          delete updated[currentRecorderId];
          return updated;
        });
        // Also update ref synchronously for immediate effect on navigation handlers
        const updatedRef = { ...recorderStatesRef.current };
        delete updatedRef[currentRecorderId];
        recorderStatesRef.current = updatedRef;
      }

      // Helper to count total replies in tree
      const countTotalRepliesInTree = (repliesArray: Reply[]): number => {
        return repliesArray.reduce((count, reply) => {
          return count + 1 + (reply.replies ? countTotalRepliesInTree(reply.replies) : 0);
        }, 0);
      };

      // Fetch updated thread data with aggressive cache-busting and retry logic
      // Database triggers might need a moment, so we retry a few times
      const fetchUpdatedThread = async (retries = 3): Promise<void> => {
        const isFirstView = !hasViewedPost(id);
        const cacheBuster = `_t=${Date.now()}`;
        const url = isFirstView 
          ? `/api/post/${id}?record_view=true&${cacheBuster}`
          : `/api/post/${id}?${cacheBuster}`;

        try {
          const updatedResponse = await fetch(url, { 
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
            }
          });

          if (!updatedResponse.ok) {
            throw new Error(`HTTP ${updatedResponse.status}`);
          }

          const updatedData = await updatedResponse.json();
          
          // Check if our new reply is in the response
          // Use refs to get current values
          const currentPost = postRef.current;
          const currentReplies = repliesRef.current;
          const currentReplyCount = currentPost?.reply_count || 0;
          const currentTotalReplies = countTotalRepliesInTree(currentReplies);
          const newTotalReplies = countTotalRepliesInTree(updatedData.replies || []);
          
          // Check if reply count increased or total replies increased
          const hasNewReply = updatedData.post.reply_count > currentReplyCount || 
                             newTotalReplies > currentTotalReplies;
          
          // If we don't see the new reply and have retries left, wait a bit and retry
          if (!hasNewReply && retries > 0) {
            console.log(`Reply not yet visible in response, retrying... (${retries} retries left)`);
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
            return fetchUpdatedThread(retries - 1);
          }
          
          // Update state with fresh data
          setPost(updatedData.post);
          setReplies(updatedData.replies || []);
          
          // Update refs to keep them in sync
          postRef.current = updatedData.post;
          repliesRef.current = updatedData.replies || [];
          
          // Update page title
          document.title = `${updatedData.post.pseudonym} on Vocly`;
          
          // Mark as viewed
          markPostAsViewed(id);
          
          // Highlight the newly created reply (without scrolling)
          // Highlight persists until user highlights another reply or clicks elsewhere
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            setHighlightedReplyId(newReplyId);
          });
        } catch (error) {
          console.error('Failed to fetch updated thread:', error);
          if (retries > 0) {
            // Retry on error
            await new Promise(resolve => setTimeout(resolve, 500));
            return fetchUpdatedThread(retries - 1);
          }
          // If all retries fail, trigger an immediate poll as fallback
          console.warn('Failed to fetch updated thread after retries, triggering immediate poll');
          if (pollTriggerRef.current) {
            // Wait a bit longer for database to settle, then poll
            setTimeout(() => {
              pollTriggerRef.current?.();
            }, 1000);
          }
        }
      };

      await fetchUpdatedThread();

      // Refresh identity to update can_reroll status (now locked after first post)
      refreshIdentity();

      // Also trigger an immediate poll after a short delay as a safety net
      // This catches edge cases where the immediate fetch might still miss the reply
      setTimeout(() => {
        if (pollTriggerRef.current) {
          pollTriggerRef.current();
        }
      }, 2000);
    } catch (error) {
      console.error("Failed to publish reply:", error);
      
      // If error already has the network error message, re-throw it as-is
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("Not able to connect")) {
        throw error;
      }
      
      // Detect network errors and provide user-friendly message
      const isNetworkError = error instanceof TypeError && (
        (typeof navigator !== 'undefined' && !navigator.onLine) || // Browser reports offline
        (error.message && (
          error.message.includes('fetch failed') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.toLowerCase().includes('network')
        ))
      );
      
      if (isNetworkError) {
        throw new Error("Not able to connect. Please check your internet connection and try again.");
      }
      
      // Re-throw other errors as-is
      throw error;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Count visible replies (recursively count all replies in the filtered tree)
  // Only counts playable replies (audio_url !== null), excluding tombstones
  const countVisibleReplies = (replyList: Reply[]): number => {
    const filtered = filterDeletedLeafReplies(replyList);
    return filtered.reduce((count, reply) => {
      const childCount = reply.replies ? countVisibleReplies(reply.replies) : 0;
      // Only count this reply if it's playable (not a tombstone)
      // Always count children recursively (they may be playable even if parent is a tombstone)
      const replyCount = reply.audio_url !== null ? 1 : 0;
      return count + replyCount + childCount;
    }, 0);
  };

  const visibleReplyCount = countVisibleReplies(replies);

  const handleShare = async () => {
    const url = `${window.location.origin}/s/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      alert("Failed to copy link. Please try again.");
    }
  };

  const handleShareToX = () => {
    const url = `${window.location.origin}/s/${id}`;
    
    // Check if current user is the post author
    const isAuthor = identity?.user_id === post?.user_id;
    
    const text = isAuthor
      ? `Just posted on Vocly 🎙️\n${url}`
      : post
      ? `Worth a listen: ${post.pseudonym} on Vocly 🎙️\n${url}`
      : `Check out this voice post on Vocly 🎙️\n${url}`;
      
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(tweetUrl, "_blank");
  };

  const handleReportRequest = (postId: string, pseudonym: string) => {
    setReportTarget({ postId, pseudonym });
  };

  const handleMainPostReply = async (audio: EncodedAudio) => {
    if (post) {
      await handleReplyPublish(audio, post.id);
      setOpenRecorderId(null);
    }
  };

  // Continue opening recorder after confirmation (or if no confirmation needed)
  const continueOpenRecorder = useCallback(async (id: string | "main") => {
    // Clear any previous rate limit errors
    if (id === "main") {
      setRateLimitError(null);
    } else {
      setReplyRateLimitErrors(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }

    // Check rate limit before opening recorder
    try {
      const localUserId = getLocalUserId();
      if (!localUserId) {
        // If we can't get local user ID, allow opening (fail open)
        startTransition(() => {
          setOpenRecorderId(id);
        });
        return;
      }

      if (!post) {
        // If post is not loaded yet, allow opening (fail open)
        startTransition(() => {
          setOpenRecorderId(id);
        });
        return;
      }

      const threadId = post.id; // For replies, threadId is the root post id
      const response = await fetch(
        `/api/rate-limit/check?threadId=${encodeURIComponent(threadId)}&checkType=reply&local_user_id=${encodeURIComponent(localUserId)}`
      );

      if (!response.ok) {
        // If check fails, still allow opening (fail open)
        console.error("Rate limit check failed:", response.statusText);
        startTransition(() => {
          setOpenRecorderId(id);
        });
        return;
      }

      const data = await response.json();
      if (!data.allowed) {
        // Rate limited - show error and don't open recorder
        const errorMessage = data.error || "You've reached the reply limit for this thread.";
        if (id === "main") {
          setRateLimitError(errorMessage);
        } else {
          setReplyRateLimitErrors(prev => ({ ...prev, [id]: errorMessage }));
        }
        return;
      }

      // Rate limit check passed - open recorder
      startTransition(() => {
        setOpenRecorderId(id);
      });
    } catch (error) {
      // If check fails, still allow opening (fail open)
      console.error("Rate limit check error:", error);
      startTransition(() => {
        setOpenRecorderId(id);
      });
    }
  }, [post]);

  // Keep ref in sync with function
  useEffect(() => {
    continueOpenRecorderRef.current = continueOpenRecorder;
  }, [continueOpenRecorder]);

  // Handler to open a recorder (closes any previously open one)
  // Use startTransition to mark lazy component loading as non-urgent, reducing TBT
  const handleOpenRecorder = useCallback(async (id: string | "main") => {
    // Check if there's an active recording or preview that would be lost
    if (openRecorderId !== null && openRecorderId !== id) {
      const currentState = recorderStates[openRecorderId] || "idle";
      if (
        currentState === "recording" ||
        currentState === "preview" ||
        currentState === "uploading"
      ) {
        // Show modal instead of window.confirm
        // This is opening a NEW recorder (not closing), so use "Starting a new recording" message
        setPendingRecorderAction({ 
          action: () => {
            // User confirmed - clear the old recorder state before opening new one
            setRecorderStates(prev => {
              const updated = { ...prev };
              delete updated[openRecorderId];
              return updated;
            });
            // Continue with opening the recorder (will be called by modal's onConfirm)
            // Use ref to ensure function is accessible
            if (continueOpenRecorderRef.current) {
              void continueOpenRecorderRef.current(id);
            }
          },
          id: id,
          state: currentState as "recording" | "preview",
          isClosing: false // Opening a new recorder
        });
        setRecordingLossModalOpen(true);
        return; // Wait for user confirmation
      }
    }

    // No active recording/preview - proceed normally
    void continueOpenRecorder(id);
  }, [openRecorderId, recorderStates, continueOpenRecorder]);

  // Handler to close a recorder (called when X button is clicked)
  const handleCloseRecorder = useCallback(() => {
    const currentId = openRecorderId;
    if (!currentId) return;
    
    // Check if there's an active recording or preview that would be lost
    const currentState = recorderStates[currentId] || "idle";
    if (
      currentState === "recording" ||
      currentState === "preview" ||
      currentState === "uploading"
    ) {
      // Show confirmation modal before closing
      // This is closing the recorder (X button), so use "Leaving now" message
      setPendingRecorderAction({
        action: () => {
          setOpenRecorderId(null);
          setRecorderStates(prev => {
            const updated = { ...prev };
            delete updated[currentId];
            return updated;
          });
        },
        id: currentId,
        state: currentState as "recording" | "preview",
        isClosing: true // Closing the recorder
      });
      setRecordingLossModalOpen(true);
      return;
    }
    
    // No active recording/preview - close immediately
    setOpenRecorderId(null);
    setRecorderStates(prev => {
      const updated = { ...prev };
      delete updated[currentId];
      return updated;
    });
  }, [openRecorderId, recorderStates]);

  // Memoized callback for recorder state changes to prevent infinite loops
  const handleRecorderStateChange = useCallback((recorderId: string, state: "idle" | "recording" | "processing" | "preview" | "uploading") => {
    setRecorderStates(prev => {
      const updated = { ...prev, [recorderId]: state };
      // Keep ref in sync for synchronous access
      recorderStatesRef.current = updated;
      return updated;
    });
  }, []);

  // Memoized callback for main post recorder state changes
  const handleMainRecorderStateChange = useCallback((state: "idle" | "recording" | "processing" | "preview" | "uploading") => {
    handleRecorderStateChange("main", state);
  }, [handleRecorderStateChange]);

  // Keep recorderStatesRef in sync with recorderStates for synchronous access
  useEffect(() => {
    recorderStatesRef.current = recorderStates;
  }, [recorderStates]);

  // Helper function to scroll main recorder into view
  const scrollMainRecorderIntoView = useCallback(() => {
    const el = mainRecorderElementRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 32;

    if (rect.left < 0 || rect.right > viewportWidth) {
      return;
    }

    let delta = 0;
    if (rect.top < padding) {
      delta = rect.top - padding;
    } else if (rect.bottom > viewportHeight - padding) {
      delta = rect.bottom - (viewportHeight - padding);
    }

    if (delta !== 0) {
      window.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, []);

  // Reset auto-scroll flag when main recorder state changes (FR-6.9)
  useEffect(() => {
    const isMainRecorderOpen = openRecorderId === "main";
    if (isMainRecorderOpen) {
      // Reset scroll flag when recorder opens
      mainRecorderAutoScrollFiredRef.current = false;
    } else {
      // Reset scroll flag when recorder closes (ensures scroll fires once per open)
      mainRecorderAutoScrollFiredRef.current = false;
    }
  }, [openRecorderId]);

  // Auto-scroll when main recorder opens/expands (FR-6.8)
  useEffect(() => {
    const isMainRecorderOpen = openRecorderId === "main";
    if (!isMainRecorderOpen || mainRecorderAutoScrollFiredRef.current) {
      return;
    }

    // Wait for DOM to update and RecorderPanel to mount (lazy loaded)
    requestAnimationFrame(() => {
      setTimeout(() => {
        scrollMainRecorderIntoView();
        setTimeout(scrollMainRecorderIntoView, 250);
        mainRecorderAutoScrollFiredRef.current = true;
      }, 120);
    });
  }, [openRecorderId, scrollMainRecorderIntoView]);

  // Auto-scroll again when recording starts or preview appears (recorder height increases)
  useEffect(() => {
    const isMainRecorderOpen = openRecorderId === "main";
    if (!isMainRecorderOpen) return;
    
    const mainState = recorderStates["main"];
    
    // Trigger auto-scroll when state changes to "recording" or "preview"
    if (
      mainState === "recording" ||
      mainState === "preview" ||
      mainState === "uploading"
    ) {
      // Wait a bit for the UI to update (timer/preview to render)
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollMainRecorderIntoView();
        }, 150);
      });
    }
  }, [openRecorderId, recorderStates, scrollMainRecorderIntoView]);

  // Manage navigationConfirmed flag based on active recordings
  // navigationConfirmed should ONLY be true AFTER user confirms navigation via custom modal
  // When there's an active recording, it must be false to allow browser's native warning to appear
  useEffect(() => {
    const hasActiveRecording = Object.values(recorderStates).some(
      state =>
        state === "recording" || state === "preview" || state === "uploading"
    );
    
    // Clear flag when there's no active recording or when user confirms navigation
    // Keep flag false when recording is active (allows browser warning to work)
    if (!hasActiveRecording) {
      setNavigationConfirmed(false);
    }
    // When hasActiveRecording is true and no pending navigation, leave flag as false
    // (browser warning should be allowed to trigger)
  }, [recorderStates]);

  // Handle browser back/forward button navigation (task 7.4.2)
  // Use shared module to avoid conflicts between pages
  useEffect(() => {
    // Register our check function
    const check = () => {
      return Object.values(recorderStatesRef.current).some(
        state =>
          state === "recording" || state === "preview" || state === "uploading"
      );
    };
    
    const unregister = registerActiveRecordingCheck(check);
    
    return () => {
      unregister();
    };
  }, []);

  // Back button warning using guard entry approach
  const guardPushedRef = useRef(false);
  const handlingPopstateRef = useRef(false);
  
  // NOTE: Proactive guard entry disabled on thread page - causes recording to glitch
  // The popstate handler below will still show confirmation, but user might navigate
  // away briefly before being able to cancel (less ideal but recording works)
  useEffect(() => {
    const isRecording = Object.values(recorderStates).some(
      state =>
        state === "recording" || state === "preview" || state === "uploading"
    );
    
    if (!isRecording && guardPushedRef.current) {
      guardPushedRef.current = false;
    }
  }, [recorderStates]);
  
  // Popstate handler - for thread page, use history.go(1) instead of pushState
  // because pushState triggers Next.js to update searchParams, causing re-renders
  useEffect(() => {
    let skipNextPopstate = false;
    
    const handlePopState = (e: PopStateEvent) => {
      if (handlingPopstateRef.current) return;
      
      // Skip if we triggered this navigation ourselves
      if (skipNextPopstate) {
        skipNextPopstate = false;
        return;
      }
      
      const isGuardState = e.state && e.state.__vocly_guard__;
      
      // If navigating TO guard, mark it
      if (isGuardState) {
        guardPushedRef.current = true;
        return;
      }
      
      // Navigated away from guard - check if recording active
      const isRecording = Object.values(recorderStatesRef.current).some(
        state =>
          state === "recording" || state === "preview" || state === "uploading"
      );
      
      if (!isRecording) {
        guardPushedRef.current = false;
        return;
      }
      
      // Recording active, user pressed back
      handlingPopstateRef.current = true;
      
      const confirmed = window.confirm(
        "You have a recording in progress.\nLeaving now will discard it."
      );
      
      if (!confirmed) {
        // Use history.go(1) to go forward (undo the back) instead of pushState
        // pushState causes Next.js to re-render the page on thread pages
        skipNextPopstate = true;
        history.go(1);
      }
      // If confirmed, navigation already happened
      
      handlingPopstateRef.current = false;
    };
    
    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, []);


  // Handle delete post
  const handleDeletePost = async () => {
    if (!post) return;

    // Stop audio immediately to prevent abort errors when file is deleted
    if (postPlayerRef.current) {
      postPlayerRef.current.pause();
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      const localUserId = getLocalUserId();
      if (!localUserId) {
        throw new Error("Unable to find your device ID. Please refresh and try again.");
      }

      const response = await fetch(`/api/post/${post.slug}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          local_user_id: localUserId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete post");
      }

      // Update post state to show tombstone
      const updatedPost = {
        ...post,
        audio_url: null as any, // Mark as deleted
      };
      setPost(updatedPost);
      postRef.current = updatedPost;

      // Show success message
      setDeleteSuccess(true);
      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (err) {
      // Detect network errors and provide user-friendly message
      const isNetworkError = err instanceof TypeError && (
        err.message.includes('fetch failed') ||
        err.message.includes('Failed to fetch') ||
        err.message.includes('NetworkError') ||
        err.message.toLowerCase().includes('network')
      );
      
      const errorMessage = err instanceof Error ? err.message : "Failed to delete post";
      
      // Treat "Unauthorized" as network error since user should be authorized if they can see delete button
      // (The API returns 403 for DB connection failures, but from user's perspective it's a connection issue)
      const isLikelyNetworkError = isNetworkError || errorMessage === "Unauthorized";
      
      const message = isLikelyNetworkError
        ? "Not able to connect. Please check your internet connection and try again."
        : errorMessage;
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete reply
  const handleDeleteReply = async (replyId: string) => {
    try {
      const localUserId = getLocalUserId();
      if (!localUserId) {
        throw new Error("Unable to find your device ID. Please refresh and try again.");
      }

      // Find the reply to get its slug
      const findReply = (replies: Reply[], id: string): Reply | null => {
        for (const reply of replies) {
          if (reply.id === id) return reply;
          if (reply.replies) {
            const found = findReply(reply.replies, id);
            if (found) return found;
          }
        }
        return null;
      };

      const reply = findReply(replies, replyId);
      if (!reply || !reply.slug) {
        throw new Error("Reply not found");
      }

      const response = await fetch(`/api/post/${reply.slug}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          local_user_id: localUserId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete reply");
      }

      // Update reply in state to show tombstone (set audio_url to null)
      const updateReply = (replies: Reply[], id: string): Reply[] => {
        return replies.map((r) => {
          if (r.id === id) {
            return { ...r, audio_url: null as any };
          }
          if (r.replies) {
            return { ...r, replies: updateReply(r.replies, id) };
          }
          return r;
        });
      };

      setReplies((prev) => {
        const updated = updateReply(prev, replyId);
        // Update ref to keep it in sync
        repliesRef.current = updated;
        return updated;
      });
    } catch (err) {
      throw err; // Re-throw to let ReplyCard handle the error
    }
  };

  // Loading state - Use skeleton that matches final layout to prevent CLS
  if (loading) {
    return (
      <div className="flex min-h-screen justify-center px-4 py-8">
        <div className="w-full max-w-[760px]">
          {/* Header skeleton */}
          <header className="mb-8 flex items-end justify-between">
            <div className="h-[56px] w-[200px] animate-pulse rounded bg-[#262636]"></div>
            <div className="h-5 w-12 animate-pulse rounded-full bg-[#262636]"></div>
          </header>
          {/* Main content skeleton - matches final card dimensions */}
          <main>
            <section className="mx-auto mb-6 max-w-[620px] rounded-[18px] border border-[rgba(255,255,255,0.02)] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] px-4 pb-5 pt-4">
              <div className="mb-2.5 flex items-center justify-between">
                <div className="h-4 w-32 animate-pulse rounded bg-[#262636]"></div>
                <div className="h-8 w-16 animate-pulse rounded-full bg-[#262636]"></div>
              </div>
              {/* Audio player skeleton - fixed height to prevent shift */}
              <div className="mb-2.5 h-20 rounded-[14px] border border-[#262636] bg-[#151522] animate-pulse"></div>
              <div className="mb-3.5 h-4 w-48 animate-pulse rounded bg-[#262636]"></div>
              <div className="h-10 w-full animate-pulse rounded-full bg-[#262636]"></div>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !post) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-black via-[#05070d] to-[#0f1013]">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold text-white">
            {error || "Post not found"}
          </h1>
          <button
            onClick={() => router.push("/")}
            className="rounded-lg bg-pink-500 px-6 py-3 font-semibold text-white transition hover:bg-pink-600"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <AudioPlayerProvider>
      {/* Main Content */}
      <main>
          {/* Root Post Card - Max 620px */}
          <section 
            id={`reply-${post.id}`}
            className={`mx-auto mb-6 max-w-[620px] ${postCardStyles.container} ${
              highlightedReplyId === post.id ? 'border-[rgba(255,62,165,0.5)] shadow-[0_0_20px_rgba(255,62,165,0.3)]' : ''
            }`}
            style={{
              background: highlightedReplyId === post.id
                ? postCardStyles.backgroundHighlighted
                : postCardStyles.background,
              willChange: highlightedReplyId === post.id ? 'box-shadow' : 'auto',
            }}
          >
            {/* Root Header */}
            <div className={`${postCardStyles.headerMargin} flex items-center justify-between`}>
              <div className="flex flex-col gap-0.5">
                <p className={postCardStyles.pseudonym}>{post.pseudonym}</p>
                <p className={postCardStyles.timestamp}>{formatTimeAgo(post.created_at)} · Original Post</p>
              </div>
              {/* Only show action buttons if post is not deleted */}
              {post.audio_url !== null && (
                <div className="flex gap-2">
                  {/* Copy link button and ellipsis menu - shared component */}
                  <PostCardActions
                    showCopyLink={true}
                    showEllipsis={identity?.user_id === post.user_id && !isAdmin}
                    onCopyLink={handleShare}
                    copied={copied}
                    ellipsisMenuOpen={ellipsisMenuOpen}
                    onEllipsisMenuOpen={() => setEllipsisMenuOpen(true)}
                    onEllipsisMenuClose={() => setEllipsisMenuOpen(false)}
                    onDelete={() => setDeleteConfirmOpen(true)}
                  />
                  {/* Hide report button when admin is logged in or user owns the post */}
                  {!isAdmin && identity?.user_id !== post.user_id && (
                    <button
                      onClick={() => handleReportRequest(post.id, post.pseudonym)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#262636] bg-[#0a0a12] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
                      title="Report this Post"
                    >
                      <Flag className="h-4 w-4 text-[#a3a3c2]" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Audio Player or Tombstone */}
            <div className={postCardStyles.audioPlayerContainer}>
              <PostPlayer
                ref={postPlayerRef}
                audioUrl={post.audio_url}
                durationMs={post.duration_ms}
                waveformUrl={post.waveform_url}
                contentType="post"
                isOwner={identity?.user_id === post.user_id}
                deletedByType={post.deleted_by_type || 'user'}
              />
            </div>

            {/* Delete Success Message */}
            {deleteSuccess && (
              <div className={postCardStyles.successMessage}>
                Post deleted successfully
              </div>
            )}

            {/* Delete Error Message */}
            {deleteError && (
              <div className={postCardStyles.errorMessage}>
                {deleteError}
                <button
                  onClick={() => setDeleteError(null)}
                  className="ml-2 underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Metrics */}
            <div className="mb-3.5 flex items-center justify-between text-xs text-[#a3a3c2]">
              <span>{post.views} listens · {visibleReplyCount} {visibleReplyCount === 1 ? "reply" : "replies"}</span>
              {post.audio_url === null && visibleReplyCount > 0 ? (
                <span className="text-[#707090]">Replies remain visible</span>
              ) : post.audio_url === null ? (
                <span className="text-[#707090]">Post removed</span>
              ) : (
                <span>Public · Anyone with the link</span>
              )}
            </div>

            {/* Share to X Button - Mockup Exact Gradient - Only show if post is not deleted and not in admin mode */}
            {post.audio_url !== null && !isAdmin && (
              <button
                onClick={handleShareToX}
                className="mb-1.5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#ff3ea5] to-[#ff6ac2] px-4 py-2.5 font-semibold text-[#160513] shadow-[0_10px_30px_rgba(255,62,165,0.5)] transition-[transform,brightness] duration-200 hover:brightness-[1.06] hover:translate-y-[-1px] hover:shadow-[0_14px_40px_rgba(255,62,165,0.7)]"
              >
                Share on 𝕏
                <ExternalLink className="h-4 w-4" />
              </button>
            )}
          </section>

          {/* Reply CTA Shell - Moved up below original Post - Only show if post is not deleted and not in admin mode */}
          {openRecorderId !== "main" && post.audio_url !== null && !isAdmin && (
            <div className="mx-auto mb-4 flex max-w-[620px] flex-col items-center gap-2">
              {rateLimitError && (
                <div className="w-full rounded-2xl border border-rose-400/20 bg-rose-500/5 p-3 text-sm text-rose-200/80">
                  {rateLimitError}
                  <button
                    onClick={() => setRateLimitError(null)}
                    className="ml-2 underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <button
                onClick={() => void handleOpenRecorder("main")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,62,165,0.2)] px-4 py-2 text-[13px] font-medium text-[#ff3ea5] transition hover:bg-[rgba(255,62,165,0.28)]"
              >
                <span className="text-[15px]">🎙</span>
                Reply with your own post
              </button>
            </div>
          )}

          {/* Inline Reply Recorder for Main Post - Moved up */}
          {openRecorderId === "main" && (
            <div ref={mainRecorderElementRef} id="recorder-main" className="mx-auto mb-4 max-w-[620px] rounded-[18px] border border-[rgba(255,255,255,0.04)] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] px-4 pb-3.5 pt-4 shadow-[0_18px_45px_rgba(0,0,0,0.6)]">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold">Record Your Reply</h3>
                  <p className="mt-1 text-sm text-[#a3a3c2]">
                    Replying to{" "}
                    <span className="font-semibold text-pink-200">
                      @{post.pseudonym}
                    </span>
                  </p>
                </div>
                {mainRecorderState === "idle" && (
                  <button
                    onClick={handleCloseRecorder}
                    className="rounded-lg border border-[#262636] bg-[#0a0a12] p-2 text-[#a3a3c2] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
                    title="Cancel"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              <Suspense fallback={<div className="py-8 text-center text-sm text-[#a3a3c2]">Loading recorder...</div>}>
                <RecorderPanel 
                  onPublish={handleMainPostReply}
                  submitButtonText="Send Reply"
                  submitButtonTextLoading="Sending Reply…"
                  onStateChange={handleMainRecorderStateChange}
                  onNavigationAttempt={(href) => {
                    const currentState = recorderStates["main"] || "idle";
                    if (
                      currentState === "recording" ||
                      currentState === "preview" ||
                      currentState === "uploading"
                    ) {
                      setPendingNavigation(href);
                      setRecordingLossModalOpen(true);
                    } else {
                      // No active recording - allow navigation
                      window.location.href = href;
                    }
                  }}
                  hasPendingNavigation={!!pendingNavigation}
                />
              </Suspense>
              
              {/* Inline Identity Line - Show only when idle/preview */}
              {(mainRecorderState === "idle" || mainRecorderState === "preview") && (
                <div
                  className={`mt-3 flex items-center justify-center gap-1 text-xs text-slate-400 ${
                    identity && !identity.can_reroll ? "opacity-50" : "opacity-65"
                  }`}
                >
                  <span>Posting as </span>
                  {identityLoading ? (
                    <span className="font-medium text-slate-100">Generating...</span>
                  ) : identity ? (
                    <>
                      <span
                        className={`font-medium ${
                          identity.can_reroll ? "text-slate-100" : "text-slate-300"
                        }`}
                      >
                        {identity.pseudonym}
                      </span>
                      {identity.can_reroll && (
                        <>
                          <span> · </span>
                          <button
                            onClick={() => void rerollPseudonym()}
                            className="text-xs text-pink-400 hover:text-pink-300 underline-offset-2 hover:underline cursor-pointer"
                          >
                            Change
                          </button>
                        </>
                      )}
                    </>
                  ) : identityError ? (
                    <span className="font-medium text-red-400">{identityError}</span>
                  ) : (
                    <span className="font-medium text-red-400">Failed to load</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Replies Section Label */}
          {visibleReplyCount > 0 && (
            <div className="mx-auto mb-3 flex max-w-[620px] items-center justify-between px-0.5 text-[13px] text-[#a3a3c2]">
              <span>Replies ({visibleReplyCount})</span>
            </div>
          )}

          {/* Replies List - Flat with Slight Indent */}
          {replies.length > 0 && (
            <section className="mx-auto mb-4 flex max-w-[620px] flex-col gap-2.5">
              {filterDeletedLeafReplies(replies).map((reply) => (
                <ReplyCard
                  key={reply.id}
                  reply={reply}
                  depth={0}
                  onPublish={handleReplyPublish}
                  parentPseudonym={post.pseudonym}
                  mainPostId={post.id}
                  collapseAllTrigger={collapseAllTrigger}
                  onReport={handleReportRequest}
                  highlightedReplyId={highlightedReplyId}
                  onHighlightParent={(parentId) => setHighlightedReplyId(parentId)}
                  openRecorderId={openRecorderId}
                  onOpenRecorder={handleOpenRecorder}
                  onCloseRecorder={handleCloseRecorder}
                  currentUserId={identity?.user_id || null}
                  onDelete={handleDeleteReply}
                  isAdmin={isAdmin}
                  onReplyPlay={markNotificationAsReadForReply}
                  rateLimitError={replyRateLimitErrors[reply.id] || null}
                  getRateLimitError={(replyId: string) => replyRateLimitErrors[replyId] || null}
                  onDismissRateLimitError={(replyId: string) => {
                    setReplyRateLimitErrors(prev => {
                      const updated = { ...prev };
                      delete updated[replyId];
                      return updated;
                    });
                  }}
                  onRecorderStateChange={handleRecorderStateChange}
                />
              ))}
            </section>
          )}

      </main>
      {reportTarget && (
        <Suspense fallback={null}>
          <ReportPostModal
            isOpen={!!reportTarget}
            postId={reportTarget.postId}
            postPseudonym={reportTarget.pseudonym}
            onClose={() => setReportTarget(null)}
          />
        </Suspense>
      )}
      <RecordingLossConfirmModal
        isOpen={recordingLossModalOpen}
        onClose={() => {
          // onClose is called when user clicks Cancel, X, or backdrop
          // (onConfirm handles Discard, which calls onClose after clearing state)
          // Capture state BEFORE clearing to know if this is a cancel
          const wasCancellingNavigation = !!pendingNavigation;
          const hadRecorderAction = !!pendingRecorderAction;
          const activeRecorderId = openRecorderId;
          const hadPendingState = !!pendingNavigation || !!pendingRecorderAction;

          // Clear state
          setRecordingLossModalOpen(false);
          setPendingRecorderAction(null);
          setPendingNavigation(null);
          // Reset navigation confirmed flag if user cancels
          setNavigationConfirmed(false);

          // Only scroll if user cancelled (not if they confirmed - onConfirm clears state first)
          // The modal calls onClose() after onConfirm(), so if onConfirm cleared the state,
          // pendingRecorderAction/pendingNavigation will be null here (but we captured hadPendingState before clearing)
          // For recorder actions: if state was cleared, the action already executed and openRecorderId changed
          // For navigation: if state was cleared, navigation already started
          // So we only scroll if this is a genuine cancel (state still exists when we check)
          const stateStillExists = !!pendingNavigation || !!pendingRecorderAction;
          
          if (hadPendingState && activeRecorderId && !stateStillExists) {
            // State was cleared by onConfirm - don't scroll (action/navigation already happened)
            return;
          }
          
          // State still exists - this is a cancel, scroll to recorder
          if (hadPendingState && activeRecorderId) {
            requestAnimationFrame(() => {
              const id = activeRecorderId === "main"
                ? "recorder-main"
                : `recorder-reply-${activeRecorderId}`;
              const el = document.getElementById(id);
              
              if (!el) return;

              // Check if recorder is already visible before scrolling
              const rect = el.getBoundingClientRect();
              const isVisible = rect.top >= 0 && 
                               rect.bottom <= window.innerHeight && 
                               rect.left >= 0 && 
                               rect.right <= window.innerWidth;

              // Only scroll if recorder is not already visible
              if (!isVisible) {
                // Use "center" for navigation cancellation, "nearest" for recorder action
                const scrollBlock = wasCancellingNavigation ? "center" : "nearest";
                el.scrollIntoView({ behavior: "smooth", block: scrollBlock });
              }
            });
          }
        }}
        onConfirm={() => {
          if (pendingRecorderAction) {
            // Execute the action (which will close old recorder and open new one)
            // Clear state BEFORE executing action to prevent scroll in onClose
            const actionToExecute = pendingRecorderAction.action;
            // Clear state immediately so onClose knows not to scroll
            setPendingRecorderAction(null);
            setRecordingLossModalOpen(false);
            // Execute action after clearing state (this will change openRecorderId)
            actionToExecute();
          } else if (pendingNavigation) {
            // User confirmed navigation - disable beforeunload warning and proceed
            setNavigationConfirmed(true);
            // Clear state immediately
            const navHref = pendingNavigation;
            setPendingNavigation(null);
            setRecordingLossModalOpen(false);
            // Use Next.js router for client-side navigation
            router.push(navHref);
          }
        }}
        title={
          pendingNavigation
            ? "Leave this page?"
            : pendingRecorderAction?.isClosing
            ? "Close recorder?"
            : "Open new recorder?"
        }
        description={
          pendingNavigation
            ? (() => {
                // Find the active recorder state for navigation warning
                const activeRecorderId = openRecorderId;
                const activeState = activeRecorderId ? (recorderStates[activeRecorderId] || "idle") : "idle";
                return activeState === "preview"
                  ? "You have a recording ready to post.\nLeaving now will discard it."
                  : "You have a recording in progress.\nLeaving now will discard it.";
              })()
            : pendingRecorderAction?.isClosing
            ? pendingRecorderAction.state === "preview"
              ? "You have a recording ready to post.\nClosing will discard it."
              : "You have a recording in progress.\nClosing will discard it."
            : pendingRecorderAction?.state === "preview"
            ? "You have a recording ready to post.\nStarting a new recording will discard it. Continue?"
            : "You have a recording in progress.\nOpening a new recorder will discard it."
        }
        confirmButtonText="Discard"
        cancelButtonText="Cancel"
      />
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeletePost}
        contentType="post"
      />
    </AudioPlayerProvider>
  );
}
